import { Button, Col, Flex, Form, InputNumber, Modal, ModalProps, notification, Radio, Row, Select, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import FanOperationType from "src/consts/FanOperationType";
import GrainType from "src/consts/GrainType";
import FanRunOperationsSettingsDTO from "src/models/FanRunOperationsSettingsDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import PremierPlusSyncSettingsRequestDTO from "src/models/PremierPlusSyncSettingsRequestDTO";
import BinApiService from "src/api/BinApiService";
import { convertCelsiusToFahrenheit, convertFahrenheitToCelsius } from "src/utils/generalUtils";
import { useCelsiusContext } from "../useCelsiusContext";
import LowGrainShutOffMode from "src/consts/LowGrainShutOffMode";
import SweepOffTime from "src/consts/SweepOffTime";
import RunFanOperationRequestDTO from "src/models/RunFanOperationRequestDTO";

const { Text } = Typography;

interface PremierPlusFanSettingsModalProps extends ModalProps{
    binDto: PremierPlusBinDTO | null;
    onClose: (result: any) => void; 
}

const PremierPlusFanSettingsModal : React.FC<PremierPlusFanSettingsModalProps> = (props) => {

    const [form] = Form.useForm<RunFanOperationRequestDTO>();
    const values = Form.useWatch([], form);
    const [isLoading, setIsLoading] = useState(false);
    
    const [selectedGrain, setSelectedGrain] = useState(GrainType.Corn);
    const handleChange = (value: GrainType) => {
        setSelectedGrain(value);
        form.setFieldValue('grainType', value); 
    };
    
    const autoGenTextStyle = {
        fontSize: "12px",
        fontWeight: "bold",
        fontFamily: 'monospace'
    };

    useEffect( () => {
        if(form.isFieldsTouched()){
            return;
        }

        if(props.binDto?.settings?.fanSettings != null){
            form.setFieldValue('grainType', props.binDto?.settings?.fanSettings?.grainType);
            form.setFieldValue('fanOperationType', props.binDto?.settings?.fanSettings?.fanOperationType);
            form.setFieldValue('useTimer', props.binDto?.settings?.fanSettings?.useTimer);
            form.setFieldValue('runFanHours', props.binDto?.settings?.fanSettings?.hours);
            form.setFieldValue('runFanMinutes', props.binDto?.settings?.fanSettings?.minutes);
            form.setFieldValue('useTempRange', props.binDto?.settings?.fanSettings?.useTempRange);
            
            if(props.binDto?.platformSettings?.useMetricSystem){
                const minTemp = convertFahrenheitToCelsius(props.binDto?.settings.fanSettings?.minWeatherTemperatureF);
                const maxTemp = convertFahrenheitToCelsius(props.binDto?.settings.fanSettings?.maxWeatherTemperatureF);
                form.setFieldValue('minWeatherTemperatureF', minTemp);
                form.setFieldValue('maxWeatherTemperatureF', maxTemp);
            }else{
                form.setFieldValue('minWeatherTemperatureF', props.binDto?.settings?.fanSettings?.minWeatherTemperatureF);
                form.setFieldValue('maxWeatherTemperatureF', props.binDto?.settings?.fanSettings?.maxWeatherTemperatureF);
            }

            form.setFieldValue('useEmcRange', props.binDto?.settings?.fanSettings?.useEmcRange);
            form.setFieldValue('minWeatherMc', props.binDto?.settings?.fanSettings?.minWeatherMc);
            form.setFieldValue('maxWeatherMc', props.binDto?.settings?.fanSettings?.maxWeatherMc);  
        }

        if(props?.binDto?.settings?.fanSettings?.grainType != null){
            handleChange(props?.binDto?.settings?.fanSettings?.grainType); 
        }
        if(props.binDto?.platformSettings?.useMetricSystem){
            form.setFieldValue('maxWeatherTemperatureF', convertFahrenheitToCelsius(props.binDto?.settings?.fanSettings?.maxWeatherTemperatureF));
            form.setFieldValue('minWeatherTemperatureF', convertFahrenheitToCelsius(props.binDto?.settings?.fanSettings?.minWeatherTemperatureF));
        }
    }, [props?.binDto]);

    const handleClose = () => {
        props.onClose(null);
    }

    const onFinish = async (data: RunFanOperationRequestDTO) => {
        data.grainType = selectedGrain;
        
        if(values.fanOperationType === FanOperationType.Automatic){
            if( (values.useTimer == null || values.useTimer === false) && (values.useEmcRange == null || values.useEmcRange === false) 
                && (values.useTempRange == null || values.useTempRange === false) ) {
                    notification.error({message: "To use Auto, you must have at least 1 automation used"});
                    return;
            }
        }

        if(data.useTimer){
            if(data.runFanMinutes <= 0 && data.runFanHours <=0){
                notification.error({ message: 'Running with fan timers can not be 0 minutes and hours' });
                return;
            }
        }

        if(data.useEmcRange){
            if(data.maxWeatherMc <= 0 && data.minWeatherMc <= 0){
                notification.error({ message: 'Running with EMC range can not be 0 for min and max' });
                return;
            }

            if(!(data.maxWeatherMc > data.minWeatherMc)){
                notification.error({ message: 'Minimum weather MC can not be more than maximum weather MC setpoint' });
                return;
            }
        }

        if(data.useTempRange){
            if(data.maxWeatherTemperatureF <= 0 && data.minWeatherTemperatureF <= 0){
                notification.error({ message: 'Running with temp range can not be 0 for min and max' });
                return;
            }

            if(!(data.maxWeatherTemperatureF > data.minWeatherTemperatureF)){
                notification.error({ message: 'Minimum weather temp can not be more than maximum weather temp setpoint' });
                return;
            }
        }

        try{

            setIsLoading(true);
            if(props.binDto?.deviceId == null){
                return;
            }

            if(data.useTempRange && props.binDto?.platformSettings?.useMetricSystem){
                data.minWeatherTemperatureF = convertCelsiusToFahrenheit(values.minWeatherTemperatureF)!;
                data.maxWeatherTemperatureF = convertCelsiusToFahrenheit(values.maxWeatherTemperatureF)!;
            }

            const request : RunFanOperationRequestDTO = {
                grainType: data.grainType,
                fanOperationType: data.fanOperationType,
                useTimer: data.useTimer,
                runFanHours: data.runFanHours,
                runFanMinutes: data.runFanMinutes,
                useTempRange: data.useTempRange,
                minWeatherTemperatureF: data.minWeatherTemperatureF,
                maxWeatherTemperatureF: data.maxWeatherTemperatureF,
                useEmcRange: data.useEmcRange,
                minWeatherMc: data.minWeatherMc,
                maxWeatherMc: data.maxWeatherMc
            }
            
            if(props.binDto?.fanRunOperation?.fanOperationType == FanOperationType.Off){
                const result = await BinApiService.premierPlus_RunFanOperation(props.binDto?.binId ?? 0, request);
            }else{
                const result = await BinApiService.premierPlus_UpdateFanOperation(props.binDto?.binId ?? 0, request);
            }

            notification.success({ message: 'Settings successfully set'});
            props.onClose(null);

        }catch(err){
            notification.error({message: 'Could not set drying settings'});
        }finally{
            setIsLoading(false);
        }

        props.onClose(null);
    }

    return(
        <Modal
        {...props}
        title={<p>Fan Settings</p>}
        destroyOnClose
        loading={isLoading}
        footer={null}
        width={'100%'}
        style={{ maxWidth: '900px' }}
        >  
            <Flex
            vertical
            gap="middle"
            style={{ height: "100%", width: "100%" }}
            >
                <Form 
                form={form} 
                onFinish={onFinish}>
                    <Form.Item
                    name={"fanOperationType"}
                    >
                        <Radio.Group 
                        buttonStyle={"solid"} 
                        block 
                        optionType="button"
                        defaultValue={FanOperationType.On}
                        >
                            <Radio value={FanOperationType.On}>ON</Radio>
                            <Radio value={FanOperationType.Automatic}>AUTO</Radio>
                        </Radio.Group>
                    </Form.Item>


                    {values?.fanOperationType === FanOperationType.Automatic && (
                        <>
                            <Text>
                                Select Grain Type
                            </Text>
                            <Form.Item name={"grainType"}>
                                <Select
                                placeholder="Select Grain Type"
                                style={{
                                    width: "400px",
                                }}
                                options={[
                                    { value: GrainType.Barley, label: "Barley" },
                                    { value: GrainType.Canola, label: "Canola" },
                                    { value: GrainType.Corn, label: "Corn" },
                                    { value: GrainType.HardSpringWheat, label: "HardSpringWheat" },
                                    { value: GrainType.HardWinterWheat, label: "HardWinterWheat" },
                                    { value: GrainType.LongGrainRoughRice, label: "LongGrainRoughRice" },
                                    { value: GrainType.MediumGrainRoughRice, label: "MediumGrainRoughRice" },
                                    { value: GrainType.Oats, label: "Oats" },
                                    { value: GrainType.Rape, label: "Rapeseed" },
                                    { value: GrainType.Rye, label: "Rye" },
                                    { value: GrainType.ShortGrainRoughRice, label: "ShortGrainRoughRice" },
                                    { value: GrainType.SoftWinterWheat, label: "SoftWinterWheat" },
                                    { value: GrainType.Sorghum, label: "Milo / Sorghum" },
                                    { value: GrainType.Soybeans, label: "Soybeans" },
                                    { value: GrainType.Sunflower, label: "Sunflower" },
                                    { value: GrainType.Wheat, label: "Wheat" },
                                    { value: GrainType.WheatDurum, label: "WheatDurum" },
                                ]}
                                />
                            </Form.Item>

                            <Row gutter={24}>
                                <Col span={10}>
                                    <Text>Use Fan Timer</Text>
                                    <Form.Item name={"useTimer"}>
                                        <Radio.Group
                                        buttonStyle={"solid"}
                                        defaultValue={false}
                                        block
                                        optionType="button"
                                        >
                                        <Radio value={false}>NO</Radio>
                                        <Radio value={true}>YES</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>

                                {values?.useTimer && (
                                <Col>
                                    <Text>Enter Duration</Text>
                                    <Row gutter={12}>
                                    <Col>
                                        <Form.Item
                                        name={"runFanHours"}
                                        >
                                        <InputNumber
                                            controls={false}
                                            max={999}
                                            min={0}
                                            inputMode="decimal"
                                            placeholder="Hours"
                                            suffix="hr"
                                        />
                                        </Form.Item>
                                    </Col>

                                    <Col>
                                        <Form.Item
                                        name="runFanMinutes"
                                        dependencies={["useFanTimer"]}
                                        >
                                        <InputNumber
                                            controls={false}
                                            inputMode="decimal"
                                            max={999}
                                            min={0}
                                            placeholder="Minutes"
                                            suffix="min"
                                        />
                                        </Form.Item>
                                    </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>

                            <Row gutter={24}>
                                <Col span={10}>
                                    <Text>Use EMC Set Point</Text>
                                    <Form.Item name={"useEmcRange"}>
                                        <Radio.Group
                                        buttonStyle={"solid"}
                                        block
                                        defaultValue={false}
                                        optionType="button"
                                        >
                                        <Radio value={false}>NO</Radio>
                                        <Radio value={true}>YES</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>

                                {values?.useEmcRange && (
                                <Col>
                                    <Text>Enter EMC Range (%)</Text>
                                    <Row gutter={12}>
                                    <Col>
                                        <Form.Item
                                        name={"minWeatherMc"}
                                        >
                                        <InputNumber
                                            controls={false}
                                            inputMode="decimal"
                                            max={100}
                                            min={0}
                                            placeholder="Min"
                                        />
                                        </Form.Item>
                                    </Col>

                                    <Col>
                                        <Form.Item
                                        name={"maxWeatherMc"}
                                        >
                                        <InputNumber
                                            controls={false}
                                            inputMode="decimal"
                                            max={100}
                                            min={0}
                                            placeholder="Max"
                                        />
                                        </Form.Item>
                                    </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>

                            <Row gutter={24}>
                                <Col span={10}>
                                    <Text>Use Temp Set Point</Text>
                                    <Form.Item name={"useTempRange"}>
                                        <Radio.Group
                                        buttonStyle={"solid"}
                                        block
                                        defaultValue={false}
                                        optionType="button"
                                        >
                                            <Radio value={false}>NO</Radio>
                                            <Radio value={true}>YES</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>

                                {values?.useTempRange && (
                                <Col>
                                    <Text>{props.binDto?.platformSettings?.useMetricSystem ? 'Enter Temp Range (°C)' : 'Enter Temp Range (°F)'}</Text>
                                    <Row gutter={12}>
                                    <Col>
                                        <Form.Item
                                        name={"minWeatherTemperatureF"}
                                        >
                                        <InputNumber
                                            inputMode="decimal"
                                            max={200}
                                            min={0}
                                            placeholder="Min"
                                        />
                                        </Form.Item>
                                    </Col>

                                    <Col>
                                        <Form.Item
                                        name={"maxWeatherTemperatureF"}
                                        >
                                        <InputNumber
                                            inputMode="decimal"
                                            max={200}
                                            min={0}
                                            placeholder="Max"
                                        />
                                        </Form.Item>
                                    </Col>
                                    </Row>
                                </Col>
                                )}
                            </Row>
                        </>
                    )}
                    
                    <span style={autoGenTextStyle}>
                        {values?.fanOperationType == FanOperationType.On &&
                        "Fan(s) will turn on when settings are submitted. "}

                        {values?.fanOperationType == FanOperationType.Automatic &&
                        `Selected Grain Type: ${values.grainType}. `}
                        {
                            values?.fanOperationType == FanOperationType.Automatic &&
                            values?.useTimer &&
                            values?.runFanHours != null &&
                            values?.runFanMinutes != null &&
                            `Fans will run for ${values?.runFanHours}:${values?.runFanMinutes.toString().padStart(2, "0")}. `
                        }
                        {
                            values?.fanOperationType == FanOperationType.Automatic &&
                            values?.useEmcRange &&
                            values?.minWeatherMc != null &&
                            values?.maxWeatherMc != null &&
                            `EMC setpoints: ${values?.minWeatherMc}% - ${values?.maxWeatherMc}%. `
                        }
                        {
                            values?.fanOperationType == FanOperationType.Automatic &&
                            values?.useTempRange &&
                            values?.minWeatherTemperatureF != null &&
                            values?.maxWeatherTemperatureF != null && 
                            `Temperature setpoints: ${values?.minWeatherTemperatureF} - ${values?.maxWeatherTemperatureF}${props.binDto?.platformSettings?.useMetricSystem ? '°C' : '°F'}. `
                        }
                    </span>
                    <br />

                    <br />
                    <Row justify={"space-between"} gutter={12}>
                        <Col span={6}>
                            <Button type='primary' onClick={handleClose} block>CANCEL</Button>
                        </Col>
                        <Col span={6}>
                            <Button type='primary' htmlType="submit" block>SUBMIT</Button>
                        </Col>
                    </Row>
                </Form>
            </Flex>
        </Modal>
    )
}

export default PremierPlusFanSettingsModal;